import { useCallback } from 'react'

import { useInfiniteQuery } from '@tanstack/react-query'

import axios from 'axios'

import { DEFAULT_IMAGE_GALLERY_FILTERS } from 'Pods/LazyLoaded/ImageGalleryPanel/constants'
import { toAspectRatio } from 'Pods/ThreeDStudio/Shared/ImageModal/Utils/toModalImage'

export const PER_PAGE = 30

const COMMUNITY_GALLERY_ENDPOINT = '/api/internal/generator/community-gallery'
const RUN_TYPE = 'image_generation'

const toImage = ({ image, aspectRatio, ...rest }) => ({
  ...rest,
  src: image,
  aspectRatio: toAspectRatio(aspectRatio),
})

// Runs without a signed image URL have nothing to render, so they are dropped.
// filterOptions carries the category chips (see useGalleryCategories).
const toPage = response => ({
  items: (response?.data ?? []).filter(item => item.image).map(toImage),
  meta: response?.meta ?? {},
  filterOptions: response?.filterOptions,
})

const fetchCommunityImages = async ({ page, searchQuery, category }) => {
  const { data } = await axios.get(COMMUNITY_GALLERY_ENDPOINT, {
    params: {
      page,
      per_page: PER_PAGE,
      run_type: RUN_TYPE,
      search_query: searchQuery || undefined,
      category: category === DEFAULT_IMAGE_GALLERY_FILTERS.category ? undefined : category,
    },
  })
  return toPage(data)
}

const getNextPageParam = (lastPage, allPages) => {
  const { currentPage, totalPages } = lastPage.meta
  return currentPage < totalPages ? allPages.length + 1 : undefined
}

// Options for the gallery's infinite query. Exported so the panel's category chips
// subscribe to the same query (see useGalleryCategories) instead of fetching their own.
export const imagesQueryOptions = ({ searchQuery, category }) => {
  const filters = { searchQuery, category }

  return {
    // Filters live in the key so any change drops the accumulated pages and
    // restarts from page 1; keepPreviousData keeps the old grid up (dimmed)
    // until the new first page lands.
    queryKey: ['communityGalleryImages', filters],
    queryFn: ({ pageParam = 1 }) => fetchCommunityImages({ page: pageParam, ...filters }),
    getNextPageParam,
    initialPageParam: 1,
    keepPreviousData: true,
  }
}

// Paged community images for the given filters. `searchQuery` arrives already
// debounced by the panel (see useDebouncedValue), so the chips and the grid share
// one query key.
function useGalleryImages({
  searchQuery = DEFAULT_IMAGE_GALLERY_FILTERS.searchQuery,
  category = DEFAULT_IMAGE_GALLERY_FILTERS.category,
} = {}) {
  const activeFilters = { searchQuery, category }

  const {
    data,
    hasNextPage,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useInfiniteQuery(imagesQueryOptions(activeFilters))

  const images = data?.pages?.flatMap(page => page.items) ?? []

  // Returns the fetch so a caller can act on the appended page - the modal steps into the
  // first image of it once it lands. The scroll sentinel ignores the result.
  const handleLoadMore = useCallback(() => fetchNextPage(), [fetchNextPage])

  return {
    images,
    hasNextPage: hasNextPage ?? false,
    handleLoadMore,
    isFetchingNextPage,
    isLoading,
    // Any in-flight request that is neither the first load nor a next page is a
    // filter-driven refetch, which is the only case the grid dims for.
    isRefetching: isFetching && !isFetchingNextPage && !isLoading,
    isError,
    activeFilters,
  }
}

export default useGalleryImages
